import PropTypes from 'prop-types';
import CarouselWithControls from './CarouselWithControls';

export default function ContactArticles({ data, isLoading }) {
  const articles = data?.slice(0, 4) || [];
  const KNOWLEDGE_BASE_URL = 'https://support.roland.com/hc/en-us/sections/200467229-Knowledge-Base';

  if (isLoading) {
    return (
      <div className='flex justify-center py-[60px]'>
        <span role='status' aria-label='Loading' className='h-12 w-12 animate-spin rounded-full border-4 border-black/10 border-t-roland-blue' />
      </div>
    );
  }

  return (
    <section className='mt-[60px] md:mt-[80px]'>
      <div className='mb-[48px] flex flex-col gap-[24px] md:flex-row md:items-start md:justify-between md:gap-[48px]'>
        <div className='flex-1'>
          <h2 className='text-center text-[26px] font-bold leading-[30px] text-black md:text-left md:text-[38px] md:leading-[44px]'>
            Popular Articles
          </h2>

          <p className='mx-auto mt-[16px] max-w-[330px] text-center text-[14px] leading-[24px] text-black md:mx-0 md:max-w-[1141px] md:text-left md:text-[18px] md:leading-[30px]'>
            Check out the latest releases, updates, and tips from the Roland Cloud universe.
          </p>
        </div>

        <div className='hidden md:flex md:shrink-0 md:justify-end'>
          <a
            href={KNOWLEDGE_BASE_URL}
            target='_blank'
            rel='noreferrer'
            className='inline-flex h-[46px] w-auto items-center justify-center rounded-[36px] bg-[#39A9F7] px-[24px] text-white transition hover:bg-[#2c9be8] hover:text-white'
          >
            <span className='text-[16px] font-medium leading-[100%] tracking-[0] md:text-[18px]'>
              View all articles
            </span>
          </a>
        </div>

      </div>

      <CarouselWithControls
        items={articles}
        slideClassName='!h-auto'
        paginationClassName='contact-articles-pagination'
        controlsClassName='md:hidden'
        breakpoints={{
          0: {
            slidesPerView: 2,
            spaceBetween: 8,
          },
          768: {
            slidesPerView: 4,
            spaceBetween: 24,
          },
        }}
        renderItem={(article) => (
          <ArticleCard article={article} />
        )}
      />

      <div className='mt-[32px] flex justify-center md:hidden'>
        <a
          href={KNOWLEDGE_BASE_URL}
          target='_blank'
          rel='noreferrer'
          className='inline-flex h-[46px] w-full items-center justify-center rounded-[36px] bg-[#39A9F7] px-[24px] text-[16px] font-medium leading-[100%] tracking-[0] text-white transition hover:bg-[#2c9be8] hover:text-white'
        >
          View all articles
        </a>
      </div>

    </section>
  );
}

function ArticleCard({ article }) {
  return (
    <article
      className='
        flex h-full flex-col
        md:w-full
      '
    >
      <div className='flex flex-1 flex-col'>
        {typeof article.vote_sum === 'number' && (
        <div className='mb-[24px] flex items-center gap-[6px]'>
          <svg
            width='15'
            height='15'
            viewBox='0 0 16 16'
            fill='none'
            xmlns='http://w3.org'
            className='shrink-0'
          >
            <path
              d='M5.333 7V14H3.333A1 1 0 0 1 2.333 13V8A1 1 0 0 1 3.333 7H5.333ZM7 14H11.293C11.78 14 12.2 13.655 12.293 13.177L13.46 7.177C13.582 6.549 13.1 6 12.46 6H9V2.667C9 2.299 8.701 2 8.333 2C8.141 2 7.959 2.083 7.833 2.228L5.333 5.111V13.333C5.333 13.701 5.632 14 6 14H7Z'
              stroke='#39A9F7'
              strokeWidth='1.5'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>

          <span className='text-[14px] font-medium leading-[15px] text-black'>
            {article.vote_sum}
          </span>
        </div>
        )}

        <div className='h-[130px] md:h-auto md:min-h-[128px]'>
          <h3
            className='
              line-clamp-5
              text-[20px] font-bold leading-[26px] text-black
              md:line-clamp-none md:text-[26px] md:leading-[32px]
            '
          >
            {article.title}
          </h3>
        </div>

        <div className='mt-[14px] min-h-[63px]'>
          {article.body && (
          <p className='line-clamp-3 text-[14px] font-normal leading-[22px] text-black'>
            {article.body
              ?.replace(/<[^>]*>/g, '')
              ?.trim()}
          </p>
          )}
        </div>
      </div>

      {article.html_url && (
        <div className='mt-[24px]'>
          <a
            href={article.html_url}
            target='_blank'
            rel='noreferrer'
            className='
              inline-flex
              h-[25px]
              items-center
              justify-center
              rounded-[20px]
              border
              border-[#39A9F7]
              px-[12px]
              text-[14px]
              font-medium
              leading-[17px]
              text-[#39A9F7]
              transition
              hover:bg-[#39A9F7]
              hover:text-white

              md:h-[46px]
              md:rounded-[36px]
              md:border-2
              md:px-[24px]
              md:text-[18px]
              md:font-bold
              md:leading-[22px]
            '
          >
            Read
          </a>
        </div>
      )}
    </article>
  );
}

ArticleCard.propTypes = {
  article: PropTypes.shape({
    id: PropTypes.number,
    title: PropTypes.string,
    body: PropTypes.string,
    snippet: PropTypes.string,
    html_url: PropTypes.string,
    vote_sum: PropTypes.number,
    vote_count: PropTypes.number,
  }).isRequired,
};

ContactArticles.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      body: PropTypes.string,
      snippet: PropTypes.string,
      html_url: PropTypes.string,
      vote_sum: PropTypes.number,
      vote_count: PropTypes.number,
    }),
  ),
  isLoading: PropTypes.bool,
};

ContactArticles.defaultProps = {
  data: null,
  isLoading: false,
};
